import { SPENT_STATUSES } from '../schema/common.js'
import type { Transaction } from '../schema/transaction.js'

/**
 * Budget arithmetic — REQUIREMENTS.md §7.4.
 *
 * Amounts are stored as `number` dollars per §5.3, but summing them directly
 * accumulates float error: 0.1 + 0.2 is famously not 0.3, and a packet total
 * that's a cent off from the statement total is a real problem when the
 * business office cross-checks them. Every sum here converts to integer cents
 * first and back once at the end.
 */

/** Dollars to integer cents, rounded half-away-from-zero. */
export function toCents(dollars: number): number {
  return Math.round(Math.abs(dollars) * 100) * Math.sign(dollars)
}

/** Integer cents back to dollars. */
export function toDollars(cents: number): number {
  return cents / 100
}

/** Sum dollar amounts without float drift. */
export function sumDollars(amounts: readonly number[]): number {
  return toDollars(amounts.reduce((total, n) => total + toCents(n), 0))
}

/** The four figures shown on a dashboard card and a code's balance strip. */
export interface BudgetTotals {
  /** Dollars authorized at fiscal-year start. */
  beginning: number
  /** Every captured transaction, all sources. Drives `remaining`. */
  spent: number
  /** `beginning - spent`. Negative means overspent. */
  remaining: number
  /** Transactions cleared by either confirmation path. */
  confirmed: number
  /** Share of the beginning balance spent, 0–1. Zero when beginning is 0. */
  spentFraction: number
  /** Number of transactions counted toward `spent`. */
  transactionCount: number
}

/**
 * A transaction counts toward Spent unless it's void. `void` means the money
 * was never actually owed; everything else — including `disputed` — represents
 * a real commitment until proven otherwise.
 */
export function countsTowardSpent(t: Pick<Transaction, 'status'>): boolean {
  return SPENT_STATUSES.includes(t.status)
}

/**
 * A transaction is confirmed once a confirmation path has cleared it — packet
 * submission for P-Card, a CSIU import match for everything else (§5.3).
 */
export function isConfirmed(t: Pick<Transaction, 'reconciledVia'>): boolean {
  return t.reconciledVia !== null
}

/**
 * Compute the four figures for one budget code.
 *
 * `transactions` must already be filtered to a single budget code and fiscal
 * year. Reversal pairs need no special handling: an AP and its negative JV both
 * count and net to zero (§5.3).
 *
 * Callers are responsible for excluding external codes — this function has no
 * opinion about them, because the packet needs their totals even though the
 * dashboard doesn't.
 */
export function computeBudgetTotals(
  beginningBalance: number,
  transactions: readonly Pick<Transaction, 'amount' | 'status' | 'reconciledVia'>[],
): BudgetTotals {
  const counted = transactions.filter(countsTowardSpent)

  const spentCents = counted.reduce((sum, t) => sum + toCents(t.amount), 0)
  const confirmedCents = counted
    .filter(isConfirmed)
    .reduce((sum, t) => sum + toCents(t.amount), 0)
  const beginningCents = toCents(beginningBalance)

  return {
    beginning: toDollars(beginningCents),
    spent: toDollars(spentCents),
    remaining: toDollars(beginningCents - spentCents),
    confirmed: toDollars(confirmedCents),
    spentFraction: beginningCents === 0 ? 0 : spentCents / beginningCents,
    transactionCount: counted.length,
  }
}

/** How a code's spending should be flagged on the dashboard (§7.4). */
export type BudgetHealth = 'ok' | 'approaching' | 'over'

export function budgetHealth(
  totals: Pick<BudgetTotals, 'remaining' | 'spentFraction'>,
  alertThresholdPercent: number,
): BudgetHealth {
  if (totals.remaining < 0) return 'over'
  if (totals.spentFraction >= alertThresholdPercent / 100) return 'approaching'
  return 'ok'
}

/**
 * Whether a budget code participates in budget math at all.
 *
 * External codes are colleagues' budgets that Matt occasionally charges on
 * their behalf (§5.2, workflow 7.7). They belong on the reconciliation packet
 * but must never appear in a dashboard total, a remaining balance, or a
 * threshold alert — the money isn't his to track.
 *
 * A domain rule rather than a UI filter, because getting it wrong in any one
 * view would silently overstate what he has left to spend.
 */
export function isBudgetTracked(
  code: Pick<BudgetCodeLike, 'external' | 'active'>,
): boolean {
  return !code.external && code.active
}

interface BudgetCodeLike {
  external: boolean
  active: boolean
}

/**
 * Split codes into the two groups every picker and screen needs: the ones
 * with balances, and the ones that only ever ride along on a packet.
 */
export function partitionBudgetCodes<T extends BudgetCodeLike>(
  codes: readonly T[],
): { owned: T[]; external: T[] } {
  return {
    owned: codes.filter((c) => !c.external && c.active),
    external: codes.filter((c) => c.external && c.active),
  }
}

/**
 * Years whose codes can seed an empty `toYear`, newest first, with a count of
 * each. Drives the "copy codes in" affordance on a year that has none.
 *
 * The account codes repeat year to year while only the balances differ, so
 * copying last year's is the fast path onto a fresh year — and first-run setup,
 * which only ever builds the *current* year, is emphatically not it (that was
 * the bug: setup from the 25-26 view kept creating 26-27's codes).
 *
 * Excludes `toYear` itself — there is nothing to copy from the year being
 * filled — and external codes, which are a colleague's rather than part of a
 * fresh year's own setup. The copy still carries external codes across, matched
 * by account code; they simply should not be what invites the click, so they
 * are not counted here.
 */
export function copyableSourceYears(
  codes: readonly { external: boolean; fiscalYear: number }[],
  toYear: number,
): { year: number; count: number }[] {
  const byYear = new Map<number, number>()
  for (const code of codes) {
    if (code.external || code.fiscalYear === toYear) continue
    byYear.set(code.fiscalYear, (byYear.get(code.fiscalYear) ?? 0) + 1)
  }
  return [...byYear.entries()]
    .map(([year, count]) => ({ year, count }))
    .sort((a, b) => b.year - a.year)
}

/**
 * Group a year's transactions by *account code*, not by the code document they
 * point at. The one grouping every screen that answers "which charges are on
 * this code" must share.
 *
 * Codes are per-year documents, so "the same code" is several documents — and a
 * charge moved across the July rollover keeps the document id it was filed
 * against, which belongs to a different year. The account code is the thing
 * that is actually the same year to year, so it is what the grouping keys on.
 * Two codes in one year cannot share an account code, so within a single year's
 * transactions nothing is counted twice.
 *
 * `accountCodeOf` resolves a document id to its account code (across years);
 * an id it cannot resolve keys under itself, so an orphaned charge still groups
 * consistently rather than vanishing. The dashboard totals and the code detail
 * page both read from this — filtering by document id in one place and grouping
 * by account code in the other is precisely what let the detail page show "no
 * transactions" on a code the dashboard had credited with nine.
 */
export function groupTransactionsByAccountCode<T extends { budgetCodeId: string }>(
  transactions: readonly T[],
  accountCodeOf: (budgetCodeId: string) => string | undefined,
): Map<string, T[]> {
  const byAccountCode = new Map<string, T[]>()
  for (const transaction of transactions) {
    const key = accountCodeOf(transaction.budgetCodeId) ?? transaction.budgetCodeId
    const list = byAccountCode.get(key)
    if (list === undefined) byAccountCode.set(key, [transaction])
    else list.push(transaction)
  }
  return byAccountCode
}

/** Total charges on an external code — no balance math applies (§8.2a). */
export function totalExternalCharges(
  transactions: readonly Pick<Transaction, 'amount' | 'status'>[],
): number {
  return sumDollars(transactions.filter(countsTowardSpent).map((t) => t.amount))
}
